import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ExitToApp as CheckoutIcon,
  SwapHoriz as SwapIcon,
  MeetingRoom as MoveToBedIcon,
  Search as SearchIcon,
  Bed as BedIcon,
  Person as PersonIcon,
  PersonAdd as ReAdmitIcon,
  Assignment as DocumentIcon,
} from '@mui/icons-material';
import { Resident, Bed } from '@hostel-manager/shared';
import ResidentForm from '../components/residents/ResidentForm';
import BedForm from '../components/residents/BedForm';
import { CreateResidentInput } from '@hostel-manager/shared/schemas';
import { CreateBedInput } from '@hostel-manager/shared';
import { fetchWithCacheFallback } from '../services/api-client.service';
import { getApiUrl } from '../config/api';
import { useMobileShell } from '../hooks/useMobileShell';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && (
        <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>{children}</Box>
      )}
    </div>
  );
}

interface BedOccupancyData {
  available: Bed[];
  occupied: Bed[];
  maintenance: Bed[];
  total: number;
}

const Residents: React.FC = () => {
  const isMobile = useMobileShell();
  const [tabValue, setTabValue] = useState(0);
  const [residents, setResidents] = useState<Resident[]>([]);
  const [bedOccupancy, setBedOccupancy] = useState<BedOccupancyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'checked_out' | 'suspended'>('active');
  
  // Dialog states
  const [admissionDialogOpen, setAdmissionDialogOpen] = useState(false);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [addBedDialogOpen, setAddBedDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedResident, setSelectedResident] = useState<Resident | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    contactNumber: '',
    emergencyContact: '',
    aadharNumber: '',
    address: '',
    monthlyRent: 0,
    remarks: '',
  });
  const [reAdmitDialogOpen, setReAdmitDialogOpen] = useState(false);
  const [reAdmitForm, setReAdmitForm] = useState({ bedNumber: '', admissionDate: new Date().toISOString().split('T')[0], monthlyRent: 0 });
  const [swapBedsDialogOpen, setSwapBedsDialogOpen] = useState(false);
  const [swapResident1, setSwapResident1] = useState<Resident | null>(null);
  const [swapResident2, setSwapResident2] = useState<Resident | null>(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferResident, setTransferResident] = useState<Resident | null>(null);
  const [transferForm, setTransferForm] = useState({
    toBedNumber: '',
    transferDate: new Date().toISOString().split('T')[0],
    rentAdjustment: 0 as number | undefined,
  });
  
  const [checkoutForm, setCheckoutForm] = useState({
    checkoutDate: new Date().toISOString().split('T')[0],
    finalSettlementAmount: 0,
    depositRefundAmount: 0,
    duesOwed: 0,
    remarks: '',
  });

  const loadResidents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (searchQuery) params.set('search', searchQuery);
      const { data } = await fetchWithCacheFallback<{ success: boolean; data: Resident[]; pagination?: { total: number } }>(
        `/api/residents?${params}`
      );
      const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
      const mapped = list.map((r: Partial<Resident> & { admissionDate?: string | Date; joiningDate?: string | Date; endDate?: string | Date; depositPaidDate?: string | Date }) => ({
        ...r,
        admissionDate: r.admissionDate ? new Date(r.admissionDate) : new Date(),
        joiningDate: r.joiningDate ? new Date(r.joiningDate) : r.admissionDate ? new Date(r.admissionDate) : new Date(),
        endDate: r.endDate ? new Date(r.endDate) : undefined,
        depositPaidDate: r.depositPaidDate ? new Date(r.depositPaidDate) : r.admissionDate ? new Date(r.admissionDate) : new Date(),
      })) as Resident[];
      setResidents(mapped);
    } catch (err) {
      setError('Failed to load residents');
      setResidents([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    loadResidents();
    loadBedOccupancy();
  }, [loadResidents]);

  const loadBedOccupancy = async () => {
    try {
      const { data } = await fetchWithCacheFallback<{ success: boolean; data: { available: Bed[]; occupied: Bed[]; maintenance: Bed[]; total: number } }>(
        '/api/residents/beds/occupancy'
      );
      const d = data?.data ?? data;
      if (!d) {
        setBedOccupancy(null);
        return;
      }
      const occupancy: BedOccupancyData = {
        available: (d.available ?? []).map((b: Bed) => ({ ...b })),
        occupied: (d.occupied ?? []).map((b: Bed) => ({ ...b })),
        maintenance: (d.maintenance ?? []).map((b: Bed) => ({ ...b })),
        total: d.total ?? (d.available?.length ?? 0) + (d.occupied?.length ?? 0) + (d.maintenance?.length ?? 0),
      };
      setBedOccupancy(occupancy);
    } catch (err) {
      console.error('Failed to load bed occupancy', err);
      setBedOccupancy(null);
    }
  };

  const handleAdmitResident = async (data: CreateResidentInput) => {
    try {
      setLoading(true);
      const payload = {
        ...data,
        admissionDate: typeof data.admissionDate === 'string' ? data.admissionDate : data.admissionDate.toISOString().split('T')[0],
        joiningDate: typeof data.joiningDate === 'string' ? data.joiningDate : data.joiningDate.toISOString().split('T')[0],
        depositPaidDate: typeof data.depositPaidDate === 'string' ? data.depositPaidDate : data.depositPaidDate.toISOString().split('T')[0],
      };
      await fetchWithCacheFallback('/api/residents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setAdmissionDialogOpen(false);
      await loadResidents();
      await loadBedOccupancy();
    } catch (err) {
      setError('Failed to admit resident');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleCheckoutResident = async () => {
    if (!selectedResident) return;
    try {
      setLoading(true);
      const payload = {
        checkoutDate: checkoutForm.checkoutDate,
        finalSettlementAmount: checkoutForm.finalSettlementAmount,
        depositRefundAmount: checkoutForm.depositRefundAmount,
        duesOwed: checkoutForm.duesOwed,
        remarks: checkoutForm.remarks || undefined,
      };
      await fetchWithCacheFallback(`/api/residents/${selectedResident.id}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setCheckoutDialogOpen(false);
      setSelectedResident(null);
      setCheckoutForm({
        checkoutDate: new Date().toISOString().split('T')[0],
        finalSettlementAmount: 0,
        depositRefundAmount: 0,
        duesOwed: 0,
        remarks: '',
      });
      await loadResidents();
      await loadBedOccupancy();
    } catch (err) {
      setError('Failed to checkout resident');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: Resident['status']) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'checked_out':
        return 'default';
      case 'suspended':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getAvailableBeds = () => {
    return bedOccupancy?.available || [];
  };

  const handleEditResident = async () => {
    if (!selectedResident) return;
    try {
      setLoading(true);
      await fetchWithCacheFallback(`/api/residents/${selectedResident.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editForm.name || undefined,
          contactNumber: editForm.contactNumber || undefined,
          emergencyContact: editForm.emergencyContact || undefined,
          aadharNumber: editForm.aadharNumber || undefined,
          address: editForm.address || undefined,
          monthlyRent: editForm.monthlyRent || undefined,
          remarks: editForm.remarks || undefined,
        }),
      });
      setEditDialogOpen(false);
      setSelectedResident(null);
      await loadResidents();
    } catch (err) {
      setError('Failed to update resident');
    } finally {
      setLoading(false);
    }
  };

  const handleReAdmit = async () => {
    if (!selectedResident || !reAdmitForm.bedNumber) return;
    try {
      setLoading(true);
      await fetchWithCacheFallback(`/api/residents/${selectedResident.id}/re-admit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bedNumber: reAdmitForm.bedNumber,
          admissionDate: reAdmitForm.admissionDate,
          monthlyRent: reAdmitForm.monthlyRent || undefined,
        }),
      });
      setReAdmitDialogOpen(false);
      setSelectedResident(null);
      await loadResidents();
      await loadBedOccupancy();
    } catch (err) {
      setError('Failed to re-admit resident');
    } finally {
      setLoading(false);
    }
  };

  const openReAdmitDialog = (resident: Resident) => {
    setSelectedResident(resident);
    setReAdmitForm({
      bedNumber: getAvailableBeds()[0]?.bedNumber ?? '',
      admissionDate: new Date().toISOString().split('T')[0],
      monthlyRent: resident.monthlyRent,
    });
    setReAdmitDialogOpen(true);
  };

  const handleSwapBeds = async () => {
    if (!swapResident1 || !swapResident2) return;
    try {
      setLoading(true);
      await fetchWithCacheFallback('/api/residents/swap-beds', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ residentId1: swapResident1.id, residentId2: swapResident2.id }),
      });
      setSwapBedsDialogOpen(false);
      setSwapResident1(null);
      setSwapResident2(null);
      await loadResidents();
      await loadBedOccupancy();
    } catch (err) {
      setError('Failed to swap beds');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferResident || !transferForm.toBedNumber) return;
    try {
      setLoading(true);
      const payload = {
        fromBedNumber: transferResident.bedNumber,
        toBedNumber: transferForm.toBedNumber,
        transferDate: transferForm.transferDate,
        ...(transferForm.rentAdjustment != null && transferForm.rentAdjustment > 0
          ? { rentAdjustment: transferForm.rentAdjustment }
          : {}),
      };
      await fetchWithCacheFallback(`/api/residents/${transferResident.id}/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setTransferDialogOpen(false);
      setTransferResident(null);
      await loadResidents();
      await loadBedOccupancy();
    } catch (err) {
      setError('Failed to transfer resident');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (resident: Resident) => {
    setSelectedResident(resident);
    setEditForm({
      name: resident.name,
      contactNumber: resident.contactNumber,
      emergencyContact: resident.emergencyContact ?? '',
      aadharNumber: resident.aadharNumber ?? '',
      address: resident.address ?? '',
      monthlyRent: resident.monthlyRent,
      remarks: resident.remarks ?? '',
    });
    setEditDialogOpen(true);
  };

  const handleAddBed = async (data: CreateBedInput) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/beds'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${response.status}`);
      }
      setAddBedDialogOpen(false);
      await loadBedOccupancy();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add bed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const ResidentsCards = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      {residents.map((resident) => (
        <Card key={resident.id} variant="outlined">
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} flexWrap="wrap" gap={1}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>{resident.name}</Typography>
                <Chip label={resident.bedNumber} color="primary" variant="outlined" size="small" sx={{ mt: 0.5 }} />
              </Box>
              <Chip label={resident.status} color={getStatusColor(resident.status)} size="small" />
            </Box>
            <Typography variant="body2" color="text.secondary">Contact: {resident.contactNumber}</Typography>
            <Typography variant="body2" color="text.secondary">Admitted: {resident.admissionDate.toLocaleDateString()} · Rent: ₹{resident.monthlyRent.toLocaleString()}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openEditDialog(resident)} sx={{ minWidth: 44, minHeight: 44 }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {resident.status === 'active' && (
                <>
                  <Tooltip title="Checkout">
                    <IconButton size="small" onClick={() => { setSelectedResident(resident); setCheckoutDialogOpen(true); }} sx={{ minWidth: 44, minHeight: 44 }}>
                      <CheckoutIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Move to Bed">
                    <IconButton size="small" onClick={() => { setTransferResident(resident); setTransferForm({ toBedNumber: getAvailableBeds()[0]?.bedNumber ?? '', transferDate: new Date().toISOString().split('T')[0], rentAdjustment: resident.monthlyRent }); setTransferDialogOpen(true); }} sx={{ minWidth: 44, minHeight: 44 }}>
                      <MoveToBedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Swap Beds">
                    <IconButton size="small" onClick={() => { setSwapResident1(resident); setSwapResident2(null); setSwapBedsDialogOpen(true); }} sx={{ minWidth: 44, minHeight: 44 }}>
                      <SwapIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {resident.status === 'checked_out' && (
                <Tooltip title="Re-admit">
                  <IconButton size="small" onClick={() => openReAdmitDialog(resident)} color="primary" sx={{ minWidth: 44, minHeight: 44 }}>
                    <ReAdmitIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const ResidentsTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Bed</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Admission Date</TableCell>
            <TableCell>Monthly Rent</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {residents.map((resident) => (
            <TableRow key={resident.id}>
              <TableCell>
                <Chip
                  label={resident.bedNumber}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {resident.name}
                  </Typography>
                  {resident.aadharNumber && (
                    <Typography variant="caption" color="textSecondary">
                      Aadhar: {resident.aadharNumber}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {resident.contactNumber}
                </Typography>
                {resident.emergencyContact && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    Emergency: {resident.emergencyContact}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {resident.admissionDate.toLocaleDateString()}
              </TableCell>
              <TableCell>₹{resident.monthlyRent.toLocaleString()}</TableCell>
              <TableCell>
                <Chip
                  label={resident.status}
                  color={getStatusColor(resident.status)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Edit Resident">
                    <IconButton size="small" onClick={() => openEditDialog(resident)} sx={{ minWidth: 44, minHeight: 44 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {resident.status === 'active' && (
                    <>
                      <Tooltip title="Checkout">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedResident(resident);
                            setCheckoutDialogOpen(true);
                          }}
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <CheckoutIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move to Bed">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTransferResident(resident);
                            const available = bedOccupancy?.available || [];
                            setTransferForm({
                              toBedNumber: available[0]?.bedNumber ?? '',
                              transferDate: new Date().toISOString().split('T')[0],
                              rentAdjustment: resident.monthlyRent || undefined,
                            });
                            setTransferDialogOpen(true);
                          }}
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <MoveToBedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Swap Beds">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSwapResident1(resident);
                            setSwapResident2(null);
                            setSwapBedsDialogOpen(true);
                          }}
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <SwapIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {resident.status === 'checked_out' && (
                    <Tooltip title="Re-admit">
                      <IconButton size="small" onClick={() => openReAdmitDialog(resident)} color="primary" sx={{ minWidth: 44, minHeight: 44 }}>
                        <ReAdmitIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="KYC Documents">
                    <IconButton size="small" sx={{ minWidth: 44, minHeight: 44 }}>
                      <DocumentIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const BedOccupancyView = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddBedDialogOpen(true)}
          sx={{ minHeight: 44 }}
        >
          Add Bed
        </Button>
      </Grid>
      {/* Summary Cards */}
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <BedIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="h6">Available</Typography>
            </Box>
            <Typography variant="h4" color="success.main">
              {bedOccupancy?.available.length || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PersonIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Occupied</Typography>
            </Box>
            <Typography variant="h4" color="primary.main">
              {bedOccupancy?.occupied.length || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Badge badgeContent="!" color="warning">
                <BedIcon color="warning" sx={{ mr: 1 }} />
              </Badge>
              <Typography variant="h6">Maintenance</Typography>
            </Box>
            <Typography variant="h4" color="warning.main">
              {bedOccupancy?.maintenance.length || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <BedIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Total</Typography>
            </Box>
            <Typography variant="h4">
              {bedOccupancy?.total || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Available Beds */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Available Beds
            </Typography>
            <Grid container spacing={2}>
              {getAvailableBeds().map((bed) => (
                <Grid item xs={12} sm={6} md={4} key={bed.bedNumber}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6">Bed {bed.bedNumber}</Typography>
                      <Typography color="textSecondary">{bed.roomLocation}</Typography>
                      <Typography color="textSecondary">Building {bed.buildingSection}</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Rent: ₹{bed.rentAmount.toLocaleString()}/month
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
            Residents Management
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage resident admissions, checkouts, and bed occupancy.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAdmissionDialogOpen(true)}
          sx={{ minHeight: 44, alignSelf: { xs: 'stretch', sm: 'auto' } }}
        >
          Add Resident
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': { height: 3 },
            '& .Mui-selected': { fontWeight: 600 },
          }}
        >
          <Tab label="Residents List" />
          <Tab label="Bed Occupancy" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Filters */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 3,
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <TextField
              placeholder="Search residents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
              sx={{ minWidth: { xs: 0, sm: 250 } }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'checked_out' | 'suspended')}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="checked_out">Checked Out</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <ResidentsCards />
          ) : (
            <ResidentsTable />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <BedOccupancyView />
        </TabPanel>
      </Card>

      {/* Admission Dialog */}
      <Dialog open={admissionDialogOpen} onClose={() => setAdmissionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Resident Admission</DialogTitle>
        <DialogContent>
          <ResidentForm
            availableBeds={getAvailableBeds()}
            onSubmit={handleAdmitResident}
            onCancel={() => setAdmissionDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Add Bed Dialog */}
      <Dialog open={addBedDialogOpen} onClose={() => setAddBedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Bed</DialogTitle>
        <DialogContent>
          <BedForm
            onSubmit={handleAddBed}
            onCancel={() => setAddBedDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Re-admit Dialog */}
      <Dialog open={reAdmitDialogOpen} onClose={() => { setReAdmitDialogOpen(false); setSelectedResident(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Re-admit Resident</DialogTitle>
        <DialogContent>
          {selectedResident && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Re-admitting: <strong>{selectedResident.name}</strong>. Assign a new bed and admission date.
              </Alert>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Bed</InputLabel>
                    <Select
                      value={reAdmitForm.bedNumber}
                      label="Bed"
                      onChange={(e) => setReAdmitForm(f => ({ ...f, bedNumber: e.target.value }))}
                    >
                      {getAvailableBeds().map((b) => (
                        <MenuItem key={b.bedNumber} value={b.bedNumber}>
                          {b.bedNumber} - {b.roomLocation} (₹{b.rentAmount}/mo)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Admission Date"
                    type="date"
                    value={reAdmitForm.admissionDate}
                    onChange={(e) => setReAdmitForm(f => ({ ...f, admissionDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Monthly Rent (₹)"
                    type="number"
                    value={reAdmitForm.monthlyRent || ''}
                    onChange={(e) => setReAdmitForm(f => ({ ...f, monthlyRent: Number(e.target.value) || 0 }))}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReAdmitDialogOpen(false); setSelectedResident(null); }} sx={{ minHeight: 44 }}>Cancel</Button>
          <Button onClick={handleReAdmit} variant="contained" color="primary" sx={{ minHeight: 44 }}>Re-admit</Button>
        </DialogActions>
      </Dialog>

      {/* Transfer / Move to Bed Dialog */}
      <Dialog open={transferDialogOpen} onClose={() => { setTransferDialogOpen(false); setTransferResident(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Move to Bed</DialogTitle>
        <DialogContent>
          {transferResident && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Moving <strong>{transferResident.name}</strong> from bed <strong>{transferResident.bedNumber}</strong> to a different bed.
              </Alert>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Target Bed</InputLabel>
                    <Select
                      value={transferForm.toBedNumber}
                      label="Target Bed"
                      onChange={(e) => setTransferForm(f => ({ ...f, toBedNumber: e.target.value }))}
                    >
                      {getAvailableBeds()
                        .filter(b => b.bedNumber !== transferResident.bedNumber)
                        .map((b) => (
                          <MenuItem key={b.bedNumber} value={b.bedNumber}>
                            {b.bedNumber} - {b.roomLocation} (₹{b.rentAmount}/mo)
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Transfer Date"
                    type="date"
                    value={transferForm.transferDate}
                    onChange={(e) => setTransferForm(f => ({ ...f, transferDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Monthly Rent (₹) - optional"
                    type="number"
                    value={transferForm.rentAdjustment || ''}
                    onChange={(e) => setTransferForm(f => ({ ...f, rentAdjustment: Number(e.target.value) || undefined }))}
                    helperText="Leave empty to keep current rent"
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTransferDialogOpen(false); setTransferResident(null); }} sx={{ minHeight: 44 }}>Cancel</Button>
          <Button onClick={handleTransfer} variant="contained" disabled={!transferForm.toBedNumber} sx={{ minHeight: 44 }}>
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Swap Beds Dialog */}
      <Dialog open={swapBedsDialogOpen} onClose={() => { setSwapBedsDialogOpen(false); setSwapResident1(null); setSwapResident2(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Swap Beds</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select two active residents to swap their bed assignments.
          </Alert>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Resident 1</InputLabel>
                <Select
                  value={swapResident1?.id ?? ''}
                  label="Resident 1"
                  onChange={(e) => {
                    const r = residents.find(x => x.id === e.target.value);
                    setSwapResident1(r ?? null);
                  }}
                >
                  {residents.filter(r => r.status === 'active').map((r) => (
                    <MenuItem key={r.id} value={r.id}>{r.name} (Bed {r.bedNumber})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Resident 2</InputLabel>
                <Select
                  value={swapResident2?.id ?? ''}
                  label="Resident 2"
                  onChange={(e) => {
                    const r = residents.find(x => x.id === e.target.value);
                    setSwapResident2(r ?? null);
                  }}
                >
                  {residents.filter(r => r.status === 'active' && r.id !== swapResident1?.id).map((r) => (
                    <MenuItem key={r.id} value={r.id}>{r.name} (Bed {r.bedNumber})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSwapBedsDialogOpen(false); setSwapResident1(null); setSwapResident2(null); }} sx={{ minHeight: 44 }}>Cancel</Button>
          <Button onClick={handleSwapBeds} variant="contained" disabled={!swapResident1 || !swapResident2} sx={{ minHeight: 44 }}>
            Swap Beds
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Resident Dialog */}
      <Dialog open={editDialogOpen} onClose={() => { setEditDialogOpen(false); setSelectedResident(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Resident</DialogTitle>
        <DialogContent>
          {selectedResident && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField fullWidth label="Name" value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Contact Number" value={editForm.contactNumber} onChange={(e) => setEditForm(f => ({ ...f, contactNumber: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Emergency Contact" value={editForm.emergencyContact} onChange={(e) => setEditForm(f => ({ ...f, emergencyContact: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Aadhar Number" value={editForm.aadharNumber} onChange={(e) => setEditForm(f => ({ ...f, aadharNumber: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Address" multiline rows={2} value={editForm.address} onChange={(e) => setEditForm(f => ({ ...f, address: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Monthly Rent (₹)" type="number" value={editForm.monthlyRent || ''} onChange={(e) => setEditForm(f => ({ ...f, monthlyRent: Number(e.target.value) || 0 }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Remarks" multiline rows={2} value={editForm.remarks} onChange={(e) => setEditForm(f => ({ ...f, remarks: e.target.value }))} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setSelectedResident(null); }} sx={{ minHeight: 44 }}>Cancel</Button>
          <Button onClick={handleEditResident} variant="contained" sx={{ minHeight: 44 }}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={checkoutDialogOpen} onClose={() => setCheckoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Checkout Resident</DialogTitle>
        <DialogContent>
          {selectedResident && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Checking out: <strong>{selectedResident.name}</strong> from bed <strong>{selectedResident.bedNumber}</strong>
              </Alert>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Checkout Date"
                    type="date"
                    value={checkoutForm.checkoutDate}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, checkoutDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Deposit Refund (₹)"
                    type="number"
                    value={checkoutForm.depositRefundAmount}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, depositRefundAmount: Number(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Dues Owed (₹)"
                    type="number"
                    value={checkoutForm.duesOwed}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, duesOwed: Number(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Final Settlement Amount (₹)"
                    type="number"
                    value={checkoutForm.finalSettlementAmount}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, finalSettlementAmount: Number(e.target.value) })}
                    helperText="Deposit refund minus any dues"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remarks"
                    multiline
                    rows={3}
                    value={checkoutForm.remarks}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, remarks: e.target.value })}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckoutDialogOpen(false)} sx={{ minHeight: 44 }}>Cancel</Button>
          <Button onClick={handleCheckoutResident} variant="contained" color="warning" sx={{ minHeight: 44 }}>
            Checkout Resident
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Residents;